<template>
  <div :class="['content mb-5', { 'policy-no-subtitle': !pageMeta.subTitle }]">
    <t-sub-nav
      class="mb-5"
      :loading="loading"
      :items="
        $secondaryNav.customerPolicyNavItems(
          $route.params.id,
          hasVirtualLocation(selectedCustomerPolicy)
        )
      "
    />

    <h2 class="mb-3">{{ this.$t("Policy Details") }}</h2>
    <b-row class="mb-6">
      <b-col sm="12" md="6" lg="6">
        <b-form-group
          v-if="!loading"
          class="mb-3"
          label-for="name"
          :label="this.$t('Policy Name')"
          :state="$validateState($v.form.name)"
        >
          <b-form-input
            data-test-id="policies-policy-details-name"
            id="name"
            v-model="form.name"
            @blur="setValidationInput('name', $event.target.value)"
            :state="$validateState($v.form.name)"
            :placeholder="this.$t('e.g. Marketing')"
          />

          <template #invalid-feedback>
            <p v-if="!$v.form.name.required">
              {{ $t("This is a required field.") }}
            </p>
          </template>
        </b-form-group>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
      <b-col sm="12" md="6" lg="6">
        <b-form-group
          v-if="!loading"
          class="mb-3"
          label-for="name"
          :label="this.$t('Description')"
          :state="$validateState($v.form.description)"
        >
          <b-form-input
            id="description"
            data-test-id="policies-policy-details-description"
            v-model="form.description"
            @blur="setValidationInput('description', $event.target.value)"
            :state="$validateState($v.form.description)"
            :placeholder="this.$t('e.g. Marketing')"
          />

          <template #invalid-feedback>
            <p v-if="!$v.form.description.minLength">
              {{ $t("Must contain at least 5 characters.") }}
            </p>
            <p v-if="!$v.form.description.maxLength">
              {{ $t("Must contain at most 200 characters.") }}
            </p>
          </template>
        </b-form-group>
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>

    <h2 class="mb-4">{{ this.$t("Filtering Options") }}</h2>
    <h3 class="mb-3">{{ this.$t("Safe Search") }}</h3>
    <p class="mb-3">
      {{
        this.$t(
          "Safe Search filters out explicit or potentially offensive search results and helps avoid most adult content search results"
        )
      }}
    </p>
    <b-row class="mb-5">
      <b-col sm="12" md="6" lg="6">
        <t-checkbox
          white-bg
          data-test-id="global-default-policy-safe-search"
          v-if="!loading"
          :label="this.$t('Safe Search')"
          :value-true="this.$t('ON')"
          :value-false="this.$t('OFF')"
          v-model="$v.form.safesearch.$model"
          id="safe_search"
        />
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>
    <h3 class="mb-3">{{ this.$t("Unclassified Traffic") }}</h3>
    <p class="mb-3">
      {{
        this.$t(
          "Unclassified traffic is from websites that are new and are not yet assigned to a category in the central web categories database"
        )
      }}
    </p>
    <b-row class="mb-6">
      <b-col sm="12" md="6" lg="6">
        <t-checkbox
          white-bg
          data-test-id="policies-policy-details-unclassified-traffic"
          v-if="!loading"
          :label="this.$t('Allow Unclassified Traffic')"
          :value-true="true"
          :value-false="false"
          v-model="$v.form.allowunclassified.$model"
          id="unclassified_traffic"
        />
        <b-skeleton
          v-if="loading"
          animation="wave"
          height="32px"
          width="100%"
        ></b-skeleton>
      </b-col>
    </b-row>

    <h2 class="mb-3">{{ this.$t("Content Filtering") }}</h2>
    <p class="mb-3">
      {{ this.$t("Choose a level of filtering to apply to your policy.") }}
    </p>
    <b-row class="mb-4" v-if="Object.keys(contentFiltering).length > 0">
      <b-col sm="12">
        <b-col>
          <t-radio-group
            v-model="form.level"
            :items="contentFiltering"
            :loading="loading"
            @change="canShowCustomise(form.level)"
          />
        </b-col>
      </b-col>
    </b-row>
    <t-save-button
      data-test-id="policies-policy-details-save"
      :form-model="$v.form"
      :fetching="fetching"
      @submit="onSubmit"
    />
  </div>
</template>

<route>
{
  "name": "Customer Policy",
  "meta": {
    "authorize" : [1, 3, 4]
  }
}
</route>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { required, minLength, maxLength } from "vuelidate/lib/validators";
import policySubPages from "@/mixins/policySubPages";

export default {
  layout: "customer",
  mixins: [policySubPages],
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("customerPolicies", [
      "fetchCustomerPolicy",
      "updateCustomerPolicy",
      "clearCache",
    ]),
    ...mapActions("customerAllowList", [
      "fetchCustomerAllowList",
      "deleteCustomerAllowListEntries",
    ]),
    ...mapActions("customerBlockList", [
      "fetchCustomerBlockList",
      "deleteCustomerBlockListEntries",
    ]),
    ...mapActions("categories", ["fetchPolicyCategories"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapMutations("modal", ["createModal", "showModal", ""]),
    canShowCustomise(val) {
      this.$v.$touch();
      if (val === "C") {
        this.customise();
      }
    },
    customise() {
      try {
        this.updateCustomerPolicy({
          id: this.selectedUser.id,
          policyId: this.selectedCustomerPolicy.id,
          policy: this.form,
        });
      } finally {
        this.createModal({
          modal: "t-categories",
          dataTestId: "modal-categories",
          title: this.$t("Custom Content Filter"),
          subtitle: this.selectedCustomerPolicy.name,
          modalData: {
            policy: this.selectedCustomerPolicy,
            categories: this.customerCategories,
            canSelectAlL: true,
          },
          props: {
            size: "xl",
            okTitle: this.$t("Save Changes"),
          },
        });
        this.showModal();
      }
    },
    setValidationInput(object, value) {
      this.form[`${object}`] = value;
      this.$v.form[`${object}`].$touch();
    },
    async onSubmit() {
      this.fetching = true;
      try {
        await this.updateCustomerPolicy({
          id: this.selectedUser.id,
          policyId: this.selectedCustomerPolicy.id,
          policy: this.form,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching = false;
        this.$v.form.$reset();
      }
    },
  },
  computed: {
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("customerPolicies", ["selectedCustomerPolicy"]),
    ...mapGetters("categories", ["customerCategories"]),
    ...mapGetters("pageMeta", ["pageMeta"]),
  },
  data: function () {
    return {
      loading: true,
      fetching: false,
      form: {
        name: null,
        description: null,
        safesearch: null,
        allowunclassified: null,
        emailnotofications: null,
        level: "M",
      },
      contentFiltering: [
        {
          text: this.$t("Essential"),
          subtitle: this.$t("Essential security protection."),
          tooltip: this.$t(
            "Blocks these categories: Anonymizer, Compromised, Criminal Skills/Hacking, Hate Speech, Phishing/Fraud, Spam, Spyware and Malicious Sites."
          ),
          testID: "policies-policy-details-content-filtering-low",
          value: "L",
          checked: false,
          icon: "shield",
        },
        {
          text: this.$t("Enhanced"),
          subtitle: this.$t(
            "Enhanced security protection, plus the blocking of adult related content."
          ),
          tooltip: this.$t(
            "Blocks Essential categories, plus Download Sites, Illegal Drugs, Online Ads, Pornography/Sex, Violence."
          ),
          testID: "policies-policy-details-content-filtering-moderate",
          value: "M",
          checked: true,
          icon: "shield-shaded",
        },
        {
          text: this.$t("Optimum"),
          subtitle: this.$t(
            "Enhanced security protection, blocking of adult related content, plus blocking of productivity inhibiting sites."
          ),
          tooltip: this.$t(
            "Blocks Essential and Enhanced categories, plus Alcohol, Dating, Gambling, Nudity, Weapons."
          ),
          testID: "policies-policy-details-content-filtering-high",
          value: "H",
          checked: false,
          icon: "shield-fill",
        },
        {
          text: this.$t("Custom"),
          subtitle: this.$t(
            "Your own custom protection level to allow or block access to selected categories by default."
          ),
          testID: "policies-policy-details-content-filtering-custom",
          value: "C",
          checked: false,
          icon: "shield-fill-plus",
          canCustomise: true,
          customiseFunction: () => this.customise(),
        },
      ],
    };
  },
  validations: {
    form: {
      name: {
        required,
      },
      description: {
        minLength: minLength(5),
        maxLength: maxLength(200),
      },
      safesearch: {},
      emailnotofications: {},
      allowunclassified: {},
      level: {},
    },
  },
  watch: {
    selectedCustomerPolicy: {
      handler() {
        this.form = {
          name: this.selectedCustomerPolicy.name,
          description: this.selectedCustomerPolicy.description,
          safesearch: this.selectedCustomerPolicy.safesearch,
          emailnotofications: this.selectedCustomerPolicy.emailnotofications,
          allowunclassified: this.selectedCustomerPolicy.allowunclassified,
          level: this.selectedCustomerPolicy.level,
        };
        this.setPageTitle(this.selectedCustomerPolicy.name);
        this.setPageSubtitle([this.selectedCustomerPolicy.description]);
      },
      deep: true,
    },
  },
  async created() {
    const reqData = {
      id: this.selectedUser.id,
      policyId: this.$route.params.id,
      tier: "user_id",
    };

    await this.fetchCustomerPolicy(reqData);
    await this.fetchPolicyCategories(reqData);

    this.loading = false;
  },
  beforeDestroy() {
    this.clearCache();
  },
};
</script>
